import type { PlatformTenant } from "./types";

const normalizedDomain = (domain: string) =>
  domain.trim().toLowerCase().replace(/\.$/u, "");

const assertUnique = (seen: Set<string>, value: string, label: string) => {
  if (seen.has(value)) throw new Error(`Duplicate ${label}: ${value}`);
  seen.add(value);
};

const assertAppUrl = (tenant: PlatformTenant) => {
  const appUrl = tenant.config.links?.app;
  if (appUrl === undefined || appUrl === null) return;

  let parsed: URL;
  try {
    parsed = new URL(appUrl);
  } catch {
    throw new Error(`Invalid app URL for tenant "${tenant.id}": ${appUrl}`);
  }

  if (!["http:", "https:"].includes(parsed.protocol)) {
    throw new Error(
      `Invalid app URL protocol for tenant "${tenant.id}": ${appUrl}`,
    );
  }
};

const assertLogoAccentColor = (tenant: PlatformTenant) => {
  const accentColor = tenant.config.branding?.logoAccentColor;
  if (
    accentColor !== undefined &&
    accentColor !== null &&
    !/^#[0-9a-f]{6}$/iu.test(accentColor)
  ) {
    throw new Error(
      `Invalid logo accent color for tenant "${tenant.id}": ${accentColor}`,
    );
  }
};

const assertQrFinderColor = (tenant: PlatformTenant) => {
  const qrFinderColor = tenant.config.theme?.qrFinderColor;
  if (
    qrFinderColor !== undefined &&
    qrFinderColor !== null &&
    !/^#[0-9a-f]{6}$/iu.test(qrFinderColor)
  ) {
    throw new Error(
      `Invalid QR finder color for tenant "${tenant.id}": ${qrFinderColor}`,
    );
  }
};

const assertPartnerLinks = (tenant: PlatformTenant) => {
  const partnerLinks = tenant.config.branding?.partnerLinks;
  if (partnerLinks === undefined || partnerLinks === null) return;

  for (const link of partnerLinks) {
    if (!link.label.trim()) {
      throw new Error(`Empty partner-link label for tenant "${tenant.id}".`);
    }

    let parsed: URL;
    try {
      parsed = new URL(link.url);
    } catch {
      throw new Error(
        `Invalid partner-link URL for tenant "${tenant.id}": ${link.url}`,
      );
    }
    if (!["http:", "https:", "mailto:", "tel:"].includes(parsed.protocol)) {
      throw new Error(
        `Invalid partner-link URL protocol for tenant "${tenant.id}": ${link.url}`,
      );
    }
  }
};

const assertLanguageMode = (tenant: PlatformTenant) => {
  const languageMode = tenant.config.localization?.languageMode;
  if (
    languageMode !== undefined &&
    !["de", "en", "both"].includes(languageMode)
  ) {
    throw new Error(
      `Invalid language mode for tenant "${tenant.id}": ${languageMode}`,
    );
  }
};

const assertObjectColors = (tenant: PlatformTenant) => {
  const { markColorMode, markCustomColor, startLineFlagColor } =
    tenant.config.objectColors ?? {};
  const hexColor = /^#[0-9a-f]{6}$/iu;

  if (markColorMode === "custom" && !markCustomColor) {
    throw new Error(`Missing custom mark color for tenant "${tenant.id}".`);
  }
  if (
    markCustomColor !== undefined &&
    markCustomColor !== null &&
    !hexColor.test(markCustomColor)
  ) {
    throw new Error(
      `Invalid custom mark color for tenant "${tenant.id}": ${markCustomColor}`,
    );
  }
  if (startLineFlagColor !== undefined && !hexColor.test(startLineFlagColor)) {
    throw new Error(
      `Invalid start-line flag color for tenant "${tenant.id}": ${startLineFlagColor}`,
    );
  }
};

export function validateTenantRegistry(
  tenants: readonly PlatformTenant[],
): readonly PlatformTenant[] {
  const ids = new Set<string>();
  const domains = new Set<string>();
  const storageNamespaces = new Set<string>();

  for (const tenant of tenants) {
    const id = tenant.id.trim();
    if (!id) throw new Error("Tenant IDs must not be empty.");
    assertUnique(ids, id, "tenant ID");

    if (tenant.domains.length === 0) {
      throw new Error(
        `Tenant "${id}" must configure at least one production domain.`,
      );
    }
    for (const domain of tenant.domains) {
      const normalized = normalizedDomain(domain);
      if (!normalized)
        throw new Error(`Tenant "${id}" contains an empty domain.`);
      assertUnique(domains, normalized, "tenant domain");
    }

    const storageNamespace = tenant.config.storageNamespace?.trim();
    if (!storageNamespace) {
      throw new Error(
        `Tenant "${id}" must configure a non-empty storage namespace.`,
      );
    }
    assertUnique(storageNamespaces, storageNamespace, "storage namespace");
    assertAppUrl(tenant);
    assertLogoAccentColor(tenant);
    assertQrFinderColor(tenant);
    assertPartnerLinks(tenant);
    assertLanguageMode(tenant);
    assertObjectColors(tenant);
  }

  return tenants;
}
